import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, concatMap, distinctUntilChanged, scan, switchMap } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { TellentGroup } from '@core/tellent-types';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { SelectGroupsService } from './select-groups.service';

@Injectable()
export class SelectGroupsController {
  private readonly takeUntilDestroyed = injectUntilDestroyed();
  private readonly itemsPerPage = 10;
  private readonly selectGroupsService = inject(SelectGroupsService);
  private readonly pagination = this.selectGroupsService.selectGroupsPagination;
  private readonly page$ = new BehaviorSubject<number>(1);

  public readonly isLoading = signal(false);
  public readonly selectedIds = new Set<string>();
  public readonly query$ = new BehaviorSubject<string>('');

  public readonly items = toSignal(
    this.query$.pipe(
      distinctUntilChanged(),
      switchMap(query =>
        this.page$.pipe(
          debounceTime(1),
          distinctUntilChanged(),
          concatMap(page =>
            this.selectGroupsService.fetchAndSelectWithPageAndQuery(page, query, this.itemsPerPage),
          ),
          scan((acc, data) => [...acc, ...data], <TellentGroup[]>[]),
        ),
      ),
      tap(() => this.isLoading.set(false)),
      this.takeUntilDestroyed,
    ),
  );
  public readonly totalCount = computed(() => this.pagination()?.totalCount);

  public toggleAssignment(groupGuid: string, isDisabled: boolean, event: Event): void {
    if (isDisabled) {
      event.stopImmediatePropagation();
      return;
    }
    this.selectedIds.has(groupGuid)
      ? this.selectedIds.delete(groupGuid)
      : this.selectedIds.add(groupGuid);
  }

  public fetchMore(event: IPageInfo) {
    if (
      this.isLoading() ||
      !this.totalCount() ||
      event.endIndex !== this.items()!.length! - 1 ||
      this.page$.value * this.itemsPerPage >= this.totalCount()!
    )
      return;
    this.page$.next(this.page$.value + 1);
  }

  public setQuery(query: string) {
    this.page$.next(1);
    this.query$.next(query);
  }
}
